import React, { useState, useEffect } from 'react';
import { updateOrder, fileToBase64 } from '../services/api';
import SuccessPopup from './SuccessPopup';
import './UpdateOrderModal.css';

const UpdateOrderModal = ({ isOpen, onClose, order, user, onSuccess, products = [] }) => {
    // Existing Affiliate State
    const [affiliateSS, setAffiliateSS] = useState(null); // File object or null
    const [affiliateSSPreview, setAffiliateSSPreview] = useState(''); // Preview URL
    // eslint-disable-next-line no-unused-vars
    const ignoreAffiliatePreview = affiliateSSPreview;
    const [affiliateLink, setAffiliateLink] = useState('');
    const [deliveredSS, setDeliveredSS] = useState(null); // File object or null
    const [deliveredSSPreview, setDeliveredSSPreview] = useState(''); // Preview URL
    // eslint-disable-next-line no-unused-vars
    const ignoreDeliveredPreview = deliveredSSPreview;

    // New Order Details State
    const [brandName, setBrandName] = useState('');
    const [productName, setProductName] = useState('');
    const [orderId, setOrderId] = useState('');
    const [orderTotal, setOrderTotal] = useState('');
    const [fullName, setFullName] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState('');
    // eslint-disable-next-line no-unused-vars
    const ignoreScreenshotPreview = screenshotPreview;

    // Common State
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    useEffect(() => {
        if (order) {
            // Reset files
            setAffiliateSS(null);
            setAffiliateSSPreview(order.affiliateSS || ''); // Show existing link as preview if it's an image
            setDeliveredSS(null);
            setDeliveredSSPreview(order.deliveredSS || '');
            setScreenshot(null);
            setScreenshotPreview(order.screenshotLink || '');

            setAffiliateLink(order.affiliateLink || '');

            setBrandName(order.brandName || '');
            setProductName(order.productName || '');
            setSelectedBrand(order.brandName || '');
            setOrderId(order.orderId || '');
            setOrderTotal(order.orderTotal || '');
            setFullName(order.fullName || '');
            setError('');
            setSuccess('');
            setShowSuccessPopup(false); 
        }
    }, [order, isOpen]);

    if (!isOpen || !order) return null;

    // Derived Logic
    const isAffiliateMode = order.orderStatus === 'Accepted';
    const canEditAffiliate = isAffiliateMode && order.affiliateStatus !== 'Paid' && order.affiliateStatus !== 'Accepted';
    const canEditOrder = !isAffiliateMode && order.affiliateStatus !== 'Paid'; // Unchecked or Rejected

    const canEdit = canEditAffiliate || canEditOrder;

    // Filter Products Logic
    const groupedProducts = products.reduce((groups, product) => {
        const brand = product.brandName || 'Other';
        if (!groups[brand]) {
            groups[brand] = [];
        }
        groups[brand].push(product);
        return groups;
    }, {});

    const brandList = Object.keys(groupedProducts).sort();
    const productsForBrand = selectedBrand ? groupedProducts[selectedBrand] || [] : [];

    const handleBrandChange = (e) => {
        const brand = e.target.value;
        setSelectedBrand(brand);
        setBrandName(brand);
        setProductName('');
    };

    const handleProductChange = (e) => {
        setProductName(e.target.value);
    };

    const handleFileChange = (e, setFile, setPreview) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setError('Please upload an image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('Image size must be less than 5MB');
                return;
            }

            setFile(file);
            setError('');

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validation for Affiliate Mode
        if (isAffiliateMode) {
            if (!affiliateSS && !order.affiliateSS) {
                setError('Please upload an Affiliate Screenshot');
                setLoading(false);
                return;
            }
            if (!deliveredSS && !order.deliveredSS) {
                setError('Please upload a Delivered Screenshot');
                setLoading(false);
                return;
            }
        }

        try {
            // Process images to base64 if new files are selected
            let affiliateSSBase64 = order.affiliateSS || '';
            if (affiliateSS) {
                affiliateSSBase64 = await fileToBase64(affiliateSS);
            }

            let deliveredSSBase64 = order.deliveredSS || '';
            if (deliveredSS) {
                deliveredSSBase64 = await fileToBase64(deliveredSS);
            }

            let screenshotBase64 = order.screenshotLink || '';
            if (screenshot) {
                screenshotBase64 = await fileToBase64(screenshot);
            }

            // Calculate lessAmount if in Order Edit Mode
            let lessAmount = order.lessAmount || 0;
            if (canEditOrder) {
                const selectedProduct = products.find(
                    p => p.brandName === brandName.trim() && p.productName === productName.trim()
                );
                lessAmount = selectedProduct ? (selectedProduct.lessAmount || 0) : 0;
            }

            const response = await updateOrder({
                buyerId: user.buyerId,
                rowIndex: order.rowIndex,
                // Send all fields
                affiliateSS: affiliateSSBase64,
                affiliateLink: affiliateLink.trim(),
                deliveredSS: deliveredSSBase64,
                brandName: brandName.trim(),
                productName: productName.trim(),
                orderId: orderId.trim(),
                orderTotal: parseFloat(orderTotal),
                lessAmount: lessAmount,
                screenshot: screenshotBase64,
                fullName: fullName.trim(),
            });

            if (response.success) {
                setShowSuccessPopup(true);
            } else {
                setError(response.message || 'Update failed');
            }
        } catch (err) {
            console.error(err);
            setError('An unexpected error occurred: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
        onSuccess(); // Close the modal and trigger parent refresh
    };

    if (showSuccessPopup) {
        return (
            <SuccessPopup
                isOpen={true}
                onClose={handleCloseSuccessPopup}
                message="Order updated successfully!"
            />
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{canEditOrder ? 'Edit Order Details' : 'Update Affiliate Details'}</h2>
                    <button className="modal-close" onClick={onClose}><i className="ri-close-line"></i></button>
                </div>

                <div className="modal-body">
                    {/* Read-Only Info */}
                    <div className="order-info-section">
                        <h3>Order Information</h3>
                        <div className="info-grid">
                            {isAffiliateMode && (
                                <>
                                    <div className="info-item">
                                        <span className="info-label">Order ID:</span>
                                        <span className="info-value">{order.orderId}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Profile Name:</span>
                                        <span className="info-value">{order.fullName}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Brand:</span>
                                        <span className="info-value">{order.brandName}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Product:</span>
                                        <span className="info-value">{order.productName}</span>
                                    </div>
                                    {order.affiliateLink && (
                                        <div className="info-item">
                                            <span className="info-label">Affiliate Link:</span>
                                            <a href={order.affiliateLink} target="_blank" rel="noopener noreferrer" className="info-value-link">
                                                {order.affiliateLink} <i className="ri-external-link-line"></i>
                                            </a>
                                        </div>
                                    )}
                                    {order.affiliateSS && (
                                        <div className="info-item">
                                            <span className="info-label">Affiliate Proof:</span>
                                            <a href={order.affiliateSS} target="_blank" rel="noopener noreferrer" className="info-value-link">
                                                View Image <i className="ri-image-line"></i>
                                            </a>
                                        </div>
                                    )}
                                </>
                            )}
                            <div className="info-item">
                                <span className="info-label">Order Status:</span>
                                <span className={`status-badge status-${order.orderStatus?.toLowerCase()}`}>
                                    {order.orderStatus}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Affiliate Status:</span>
                                <span className={`status-badge affiliate-${order.affiliateStatus?.toLowerCase()}`}>
                                    {order.affiliateStatus}
                                </span>
                            </div>

                            {/* Display Admin Comment if present */}
                            {order.comment && (
                                <div className="info-item rejection-info" style={{ gridColumn: '1 / -1', marginTop: '0.5rem', padding: '0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                                    <span className="info-label" style={{ color: '#dc2626', display: 'block', marginBottom: '0.25rem' }}>
                                        <i className="ri-chat-1-line"></i> Admin Comment:
                                    </span>
                                    <span className="info-value" style={{ color: '#b91c1c', fontWeight: '600' }}>
                                        {order.comment}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {!canEdit ? (
                        <div className="update-disabled-message">
                            {isAffiliateMode ? (
                                <p><i className="ri-error-warning-line"></i> This order cannot be edited because the affiliate status is marked as {order.affiliateStatus}.</p>
                            ) : (
                                <p><i className="ri-error-warning-line"></i> This order cannot be updated in its current state.</p>
                            )}
                        </div>
                    ) : (
                        <form id="updateOrderForm" onSubmit={handleSubmit} className="update-form">
                            {error && <div className="error-message">{error}</div>}

                            {/* ORDER DETAILS FORM */}
                            {canEditOrder && (
                                <div className="order-edit-fields">
                                    <h3>Edit Order Details</h3>

                                    <div className="form-group">
                                        <label htmlFor="fullName">Profile Name</label>
                                        <input
                                            type="text"
                                            id="fullName"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="brandName">Brand Name</label>
                                        <select
                                            id="brandName"
                                            value={selectedBrand}
                                            onChange={handleBrandChange}
                                            required
                                            className="form-select"
                                        >
                                            <option value="">-- Select Brand --</option>
                                            {brandList.map((brand) => (
                                                <option key={brand} value={brand}>{brand}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="productName">Product Name</label>
                                        <select
                                            id="productName"
                                            value={productName}
                                            onChange={handleProductChange}
                                            required
                                            disabled={!selectedBrand}
                                            className="form-select"
                                        >
                                            <option value="">-- Select Product --</option>
                                            {productsForBrand.map((product, index) => (
                                                <option key={index} value={product.productName}>
                                                    {product.productName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="orderId">Order ID</label>
                                        <input
                                            type="text"
                                            id="orderId"
                                            value={orderId}
                                            onChange={(e) => setOrderId(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="orderTotal">Order Total Amount (₹)</label>
                                        <input
                                            type="number"
                                            id="orderTotal"
                                            value={orderTotal}
                                            onChange={(e) => setOrderTotal(e.target.value)}
                                            required
                                            step="0.01"
                                            min="0"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="screenshot">Order Screenshot</label>
                                        <input
                                            type="file"
                                            id="screenshot"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setScreenshot, setScreenshotPreview)}
                                        />
                                        <small>Upload a new order screenshot to replace the existing one</small>
                                    </div>
                                </div>
                            )}

                            {/* AFFILIATE DETAILS FORM */}
                            {canEditAffiliate && (
                                <div className="affiliate-edit-fields">
                                    <h3>Affiliate Details</h3>

                                    <div className="form-group">
                                        <label htmlFor="affiliateLink">Affiliate Link <span style={{ fontWeight: 'normal', color: '#666' }}>(Optional)</span></label>
                                        <input
                                            type="url"
                                            id="affiliateLink"
                                            value={affiliateLink}
                                            onChange={(e) => setAffiliateLink(e.target.value)}
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="affiliateSS">
                                            Affiliate Screenshot {!order.affiliateSS && <span className="required">*</span>}
                                        </label>
                                        <input
                                            type="file"
                                            id="affiliateSS"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setAffiliateSS, setAffiliateSSPreview)}
                                            required={!order.affiliateSS}
                                        />
                                        <small>{order.affiliateSS ? 'Upload new file to replace existing affiliate proof' : 'Upload proof of your affiliate transaction'}</small>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="deliveredSS">
                                            Delivered Screenshot {!order.deliveredSS && <span className="required">*</span>}
                                        </label>
                                        <input
                                            type="file"
                                            id="deliveredSS"
                                            accept="image/*"
                                            onChange={(e) => handleFileChange(e, setDeliveredSS, setDeliveredSSPreview)}
                                            required={!order.deliveredSS}
                                        />
                                        <small>{order.deliveredSS ? 'Upload new file to replace existing delivery proof' : 'Upload proof that the order was delivered'}</small>
                                    </div>
                                </div>
                            )}
                        </form>
                    )}
                </div>
                
                {canEdit && (
                    <div className="modal-actions">
                        <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
                            <i className="ri-close-line"></i> Cancel
                        </button>
                        <button type="submit" form="updateOrderForm" className="btn-submit" disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="btn-spinner"></span> Updating...
                                </>
                            ) : (
                                <>
                                    <i className="ri-save-line"></i> Update Order
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateOrderModal;
